import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { espaceApi, messageErreur, ouvrirPdf, urlServeur, CLE_JETON } from "./api";
import { Connexion } from "./Connexion";
import { EnTete, RetourSite } from "./EnTete";
import { BarreOnglets, ONGLETS } from "./BarreOnglets";
import { TabAujourdhui, nonLus } from "./TabAujourdhui";
import { TabCours } from "./TabCours";
import { TabFactures } from "./TabFactures";
import { TabPlanning } from "./TabPlanning";
import { TabProfil } from "./TabProfil";
import { InstallerApp } from "./InstallerApp";
import { marquerDepuisApp } from "../components/RetourApp";
import { BoutonChoixEspace, ouvrirEspace, roleRetenu } from "../components/ChoixEspace";
import { useNavigate } from "react-router-dom";

// Connexion Google, directement chez Google : le serveur envoie vers Google, puis revient
// sur /espace avec la session dans l'adresse (#session=…), lue au chargement.
const versGoogle = () => {
  window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/espace/google/debut?retour=${encodeURIComponent(window.location.origin)}`;
};

const TITRES = {
  aujourdhui: "Accueil",
  cours: "Mes cours",
  factures: "Factures",
  planning: "Planning de l'école",
  profil: "Mon profil",
};

export default function Espace() {
  const [moi, setMoi] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState("");
  const navigate = useNavigate();
  // L'onglet ouvert est retenu le temps de la visite : en revenant d'une page du site
  // (règlement, inscription), on retrouve l'écran qu'on avait quitté.
  const [onglet, setOngletBrut] = useState(() => {
    try { return sessionStorage.getItem("espace_onglet") || "aujourdhui"; } catch { return "aujourdhui"; }
  });
  const setOnglet = useCallback((o) => {
    setOngletBrut(o);
    try { sessionStorage.setItem("espace_onglet", o); } catch { /* navigation privée */ }
  }, []);

  // L'espace est une application : l'assistant conversationnel du site public y
  // recouvrait la barre d'onglets, et le corps de page ne doit pas défiler derrière
  // l'écran (c'est la zone centrale qui défile, comme dans une app).
  useEffect(() => {
    document.body.classList.add("espace-app");
    marquerDepuisApp();
    // De retour du site : la page était peut-être zoomée ou défilée (formulaire
    // d'inscription). On remet l'écran à plat, sinon l'app s'affiche coupée à droite et
    // sans sa barre d'onglets.
    window.scrollTo(0, 0);
    const vue = document.querySelector('meta[name="viewport"]');
    const avant = vue?.getAttribute("content");
    let minuterie;
    if (vue && avant) {
      vue.setAttribute("content", `${avant}, maximum-scale=1`);
      minuterie = setTimeout(() => vue.setAttribute("content", avant), 400);
    }
    return () => {
      clearTimeout(minuterie);
      if (vue && avant) vue.setAttribute("content", avant);
      document.body.classList.remove("espace-app");
    };
  }, []);

  // Le chargement traîne (réseau faible, serveur qui redémarre) : au bout de 8 s, un
  // bouton « Recharger » plutôt qu'une roue qui tourne sans fin.
  const [lent, setLent] = useState(false);
  useEffect(() => {
    if (!chargement) return undefined;
    const t = setTimeout(() => setLent(true), 8000);
    return () => clearTimeout(t);
  }, [chargement]);

  const charger = useCallback(async () => {
    try {
      // Jamais d'attente infinie : sans réponse en 20 s, on propose de recharger.
      const { data } = await espaceApi.get("/moi", { timeout: 20000 });
      // Session prolongée par le serveur (plus d'une semaine) : on garde la neuve, la
      // famille ne retape pas de code tant qu'elle ouvre l'app au moins une fois par mois.
      if (data.session) { try { localStorage.setItem(CLE_JETON, data.session); } catch { /* rien */ } }
      setMoi(data);
      setErreur("");
      setLent(false);
    } catch (e) {
      const statut = e?.response?.status;
      if (statut === 403) setErreur(messageErreur(e));
      // Seul un 401 veut dire « plus connectée » (session expirée, mot de passe changé
      // ailleurs) : on oublie alors le jeton. Une panne, un serveur qui redémarre ou pas de
      // réseau ne renvoient pas la famille à l'écran de connexion : on propose de recharger.
      if (statut === 401) {
        try { localStorage.removeItem(CLE_JETON); } catch { /* rien */ }
      } else if (statut !== 403 && localStorage.getItem(CLE_JETON)) {
        setLent(true);
        return;
      }
      setMoi(false);
    }
    setChargement(false);
  }, []);

  // Trois façons d'arriver ici : avec un lien e-mail (?jeton=), au retour de Google
  // (#session_id=), ou déjà connectée. Les deux premières s'échangent contre une session
  // AVANT toute lecture, sinon on afficherait l'écran de connexion à quelqu'un qui vient
  // justement de se connecter.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const jeton = params.get("jeton");
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    // Retour de Google : la session est déjà ouverte par le serveur.
    if (fragment.get("session")) {
      localStorage.setItem(CLE_JETON, fragment.get("session"));
      window.history.replaceState({}, "", "/espace");
      charger();
      return;
    }
    if (fragment.get("erreur") === "google") {
      setErreur("La connexion Google n'a pas abouti. Réessaie, ou connecte-toi avec ton e-mail.");
      window.history.replaceState({}, "", "/espace");
    }

    const echanger = async () => {
      try {
        const { data } = await espaceApi.post("/session", { jeton });
        if (data.session) localStorage.setItem(CLE_JETON, data.session);
        window.history.replaceState({}, "", "/espace");
        await charger();
      } catch (e) {
        setErreur(messageErreur(e));
        setMoi(false);
        setChargement(false);
        window.history.replaceState({}, "", "/espace");
      }
    };

    if (jeton) echanger();
    else charger();
  }, [charger]);

  // Plusieurs casquettes : l'app rouvre le dernier espace choisi. La direction arrive en
  // mode école, une prof sans enfant inscrit dans son espace prof.
  useEffect(() => {
    if (!moi?.roles) return;
    const retenu = roleRetenu();
    let cible = "";
    if (retenu === "ecole" && moi.roles.ecole) cible = "ecole";
    else if (retenu === "prof" && moi.roles.prof) cible = "prof";
    else if (!retenu && moi.roles.ecole) cible = "ecole";
    else if (!retenu && !moi.rattachee && moi.roles.prof) cible = "prof";
    if (cible) ouvrirEspace(cible, navigate).catch(() => { /* reste en espace famille */ });
  }, [moi, navigate]);

  // L'app rouverte après un moment (téléphone sorti de la poche) se remet à jour toute
  // seule : un solde ou une absence ne doit jamais rester périmé à l'écran.
  useEffect(() => {
    const auRetour = () => { if (document.visibilityState === "visible" && moi) charger(); };
    document.addEventListener("visibilitychange", auRetour);
    return () => document.removeEventListener("visibilitychange", auRetour);
  }, [charger, moi]);

  // Chaque onglet repart du haut : dans une application, on n'atterrit pas au milieu de
  // l'écran précédent.
  useEffect(() => {
    document.getElementById("espace-defilement")?.scrollTo({ top: 0 });
  }, [onglet]);

  const deconnecter = async () => {
    try {
      await espaceApi.post("/deconnexion");
    } finally {
      // Toutes les clés de l'app : sur un téléphone partagé, le jeton « mode école » ou
      // « prof » ne doit pas survivre à la déconnexion.
      [CLE_JETON, "admin_token", "prof_session", "app_role"].forEach((k) => {
        try { localStorage.removeItem(k); } catch { /* rien */ }
      });
      setMoi(false);
      setOnglet("aujourdhui");
    }
  };

  const attestation = async (enfant) => {
    setErreur("");
    // Le lien signé s'ouvre partout, y compris dans l'app installée sur iPhone.
    if (enfant.attestation_lien) {
      window.open(urlServeur(enfant.attestation_lien), "_blank", "noopener");
      return;
    }
    try {
      await ouvrirPdf(`/attestation/${encodeURIComponent(enfant.id)}/pdf`);
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };

  if (chargement) {
    return (
      <div className="min-h-[100dvh] grid place-items-center px-6" data-testid="espace-chargement">
        <div className="text-center space-y-4">
          <RefreshCw size={22} className="animate-spin mx-auto" style={{ color: "var(--primary)" }} />
          {lent && (
            <>
              <p className="text-[15px] text-[var(--muted)]">Ça prend plus de temps que prévu…</p>
              <button className="btn-primary" onClick={() => window.location.reload()}
                data-testid="espace-recharger">Recharger</button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!moi) {
    return (
      <div className="min-h-[100dvh] px-5 py-10 flex flex-col justify-center"
        style={{ background: "var(--bg-soft, #EEF6F8)" }} data-testid="page-espace">
        <div className="max-w-lg w-full mx-auto space-y-5">
          <RetourSite />
          <InstallerApp />
          {erreur && (
            <div className="text-sm rounded-2xl px-4 py-3" data-testid="espace-erreur-globale"
              style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</div>
          )}
          <Connexion onGoogle={versGoogle} onConnectee={charger} />
        </div>
      </div>
    );
  }

  const ecrans = {
    aujourdhui: <TabAujourdhui moi={moi} onOnglet={setOnglet} onAttestation={attestation} onRecharger={charger} />,
    cours: <TabCours moi={moi} onAttestation={attestation} onRecharger={charger} onOnglet={setOnglet} />,
    factures: <TabFactures moi={moi} onAttestation={attestation} />,
    planning: <TabPlanning />,
    profil: <TabProfil moi={moi} onRecharger={charger} onQuitter={deconnecter} />,
  };

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden"
      style={{ background: "var(--bg-soft, #EEF6F8)" }} data-testid="espace-app-shell">
      <EnTete
        titre={TITRES[onglet] || "Accueil"}
        sousTitre={`Saison ${String(moi.saison || "").replace("-", "/")} · Semestre ${moi.semestre}`}
        responsable={moi.responsable}
        email={moi.email}
        onQuitter={deconnecter}
        extra={<BoutonChoixEspace actuel="famille" roles={{ ...(moi.roles || {}), famille: true }} />}
      />

      <main id="espace-defilement" className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        style={{ WebkitOverflowScrolling: "touch" }}>
        <div className="max-w-lg mx-auto px-5 py-5"
          style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}>
          {erreur && (
            <div className="text-sm rounded-2xl px-4 py-3 mb-5" data-testid="espace-erreur-action"
              style={{ background: "#FEF2F2", color: "#B91C1C" }}>{erreur}</div>
          )}
          {ecrans[onglet]}
        </div>
      </main>

      <BarreOnglets actif={onglet} onChange={setOnglet} solde={moi.a_payer} messages={nonLus(moi.messages)} />
    </div>
  );
}

export { ONGLETS };
